import { useState, useEffect, useCallback } from 'react';
import { UserProgress, AITier } from '../types';

interface ContextualAdvice {
  greeting: string;
  insight: string;
  suggestion?: string;
  tone: 'encouraging' | 'reflective' | 'celebratory' | 'supportive';
}

interface GamificationState {
  progress: {
    currentStreak: number;
    longestStreak: number;
    totalEntries: number;
    totalPatterns: number;
    totalDecisions: number;
    daysActive: number;
  } | null;
  aiTier: AITier;
  contextualAdvice: ContextualAdvice | null;
  isLoading: boolean;
  error: string | null;
}

interface UseGamificationResult extends GamificationState {
  refreshProgress: () => Promise<void>;
  refreshAdvice: () => Promise<void>;
  updateStreak: (currentStreak: number, longestStreak: number) => Promise<void>;
}

function calculateAITier(progress: GamificationState['progress']): AITier {
  if (!progress) return 'explorer';
  
  const { totalEntries, daysActive, currentStreak } = progress;
  
  if (totalEntries >= 100 && daysActive >= 60 && currentStreak >= 14) {
    return 'oracle';
  } else if (totalEntries >= 50 && daysActive >= 30 && currentStreak >= 7) {
    return 'companion';
  } else if (totalEntries >= 20 && daysActive >= 14) {
    return 'learner';
  }
  return 'explorer';
}

export function useGamification(): UseGamificationResult {
  const [progress, setProgress] = useState<GamificationState['progress']>(null);
  const [contextualAdvice, setContextualAdvice] = useState<ContextualAdvice | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshProgress = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/gamification/progress', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setProgress({
            currentStreak: data.currentStreak || 0,
            longestStreak: data.longestStreak || 0,
            totalEntries: data.totalEntries || 0,
            totalPatterns: data.totalPatterns || 0,
            totalDecisions: data.totalDecisions || 0,
            daysActive: data.daysActive || 0
          });
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch gamification progress:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshAdvice = useCallback(async () => {
    try {
      const response = await fetch('/api/gamification/advice', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContextualAdvice(data);
      }
    } catch (err: any) {
      console.error('Failed to fetch contextual advice:', err);
    }
  }, []);

  const updateStreak = useCallback(async (currentStreak: number, longestStreak: number) => {
    try {
      const response = await fetch('/api/gamification/streak', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentStreak, longestStreak })
      });
      
      if (response.ok) {
        const data = await response.json();
        setProgress(prev => prev ? {
          ...prev,
          currentStreak: data.currentStreak || currentStreak,
          longestStreak: data.longestStreak || longestStreak
        } : null);
      }
    } catch (err: any) {
      console.error('Failed to update streak:', err);
    }
  }, []);

  useEffect(() => {
    refreshProgress();
  }, [refreshProgress]);

  const aiTier = calculateAITier(progress);

  return {
    progress,
    aiTier,
    contextualAdvice,
    isLoading,
    error,
    refreshProgress,
    refreshAdvice,
    updateStreak
  };
}
